import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { createObjective } from '@/lib/student-objectives/student-objectives';
import { ObjectivePicker } from '@/components/student-objectives/objective-picker';
import { ObjectivePackPicker } from '@/components/student-objectives/objective-pack-picker';
import type { AppObjectiveTemplate } from '@/lib/student-objectives/objective-types';

/** Pack choisi pour un objectif « Réviser un pack » (migration 0514). */
interface PackSelection {
  templateId: string;
  packId: string;
}

/** Étape 2 : pour chaque objectif à pack, l'élève choisit un pack de son année. */
function PackSelectionStep({
  templates,
  selections,
  onChange,
}: {
  templates: AppObjectiveTemplate[];
  selections: PackSelection[];
  onChange: (templateId: string, packId: string) => void;
}) {
  return (
    <div className="space-y-6">
      <p className="text-ink-500 font-sans text-[14px]">
        Choisis le pack sur lequel tu veux travailler : ses SKILLCARDS formeront ton objectif.
      </p>
      {templates.map((t) => {
        const sel = selections.find((s) => s.templateId === t.id);
        return (
          <section key={t.id} className="space-y-3">
            {templates.length > 1 && (
              <p className="font-display text-ink-800 text-[14px] font-semibold">{t.title}</p>
            )}
            <ObjectivePackPicker
              selectedPackId={sel?.packId || null}
              onSelect={(p) => onChange(t.id, p.id)}
            />
          </section>
        );
      })}
    </div>
  );
}

export function StudentObjectivesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const studentId = user?.id ?? '';

  const { data: studentProfile } = useQuery({
    queryKey: ['student-profile', studentId],
    enabled: !!studentId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('student_profiles')
        .select('level')
        .eq('id', studentId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const studentLevel = studentProfile?.level ?? null;

  // Étape 1 : sélection des templates
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Templates complets pour les sélectionnés (nécessaires à l'étape 2)
  const [selectedTemplates, setSelectedTemplates] = useState<AppObjectiveTemplate[]>([]);
  // Packs choisis (étape 2)
  const [packSelections, setPackSelections] = useState<PackSelection[]>([]);

  function toggleId(id: string, template: AppObjectiveTemplate) {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        setSelectedTemplates((ts) => ts.filter((t) => t.id !== id));
        return prev.filter((x) => x !== id);
      }
      setSelectedTemplates((ts) => [...ts, template]);
      return [...prev, id];
    });
  }

  function handleGoToStep2() {
    // Initialise un choix de pack pour les templates qui le requièrent
    const needPack = selectedTemplates.filter((t) => t.requiresPackSelection);
    setPackSelections(needPack.map((t) => ({ templateId: t.id, packId: '' })));
    setStep(2);
  }

  function updatePackSelection(templateId: string, packId: string) {
    setPackSelections((prev) =>
      prev.map((s) => (s.templateId === templateId ? { ...s, packId } : s)),
    );
  }

  const needsPackStep = selectedTemplates.some((t) => t.requiresPackSelection);

  // Validation étape 2 : chaque objectif à pack a son pack
  const step2Valid = packSelections.every((s) => s.packId !== '');

  async function handleConfirm() {
    if (!user?.id || selectedIds.length === 0) return;

    await Promise.allSettled(
      selectedIds.map((objectiveTemplateId) => {
        const sel = packSelections.find((s) => s.templateId === objectiveTemplateId);
        const tpl = selectedTemplates.find((t) => t.id === objectiveTemplateId);
        return createObjective(supabase, user.id, {
          objectiveTemplateId,
          selectedPackId: sel?.packId || null,
          deadline: tpl?.defaultDeadline ?? null,
        });
      }),
    );

    // Rafraîchit le cache du tableau de bord pour que les nouveaux objectifs
    // apparaissent immédiatement (sinon bloqués par le staleTime global de 5 min).
    await queryClient.invalidateQueries({ queryKey: ['student-objectives'] });
    await queryClient.invalidateQueries({ queryKey: ['objectives-progress'] });

    void navigate({ to: '/student/dashboard' });
  }

  if (!studentLevel) return null;

  const templatesNeedingPack = selectedTemplates.filter((t) => t.requiresPackSelection);

  return (
    <div className="pb-safe mx-auto max-w-2xl px-4 py-6 sm:py-8">
      {/* En-tête */}
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          className="text-ink-500 -ml-2 mb-3"
          onClick={() => (step === 2 ? setStep(1) : navigate({ to: '/student/dashboard' }))}
        >
          ← {step === 2 ? 'Retour' : 'Accueil'}
        </Button>
        <h1 className="text-ink-900 text-2xl font-bold">
          {step === 1 ? 'Ajouter des objectifs' : 'Choisir un pack'}
        </h1>
        {step === 1 && (
          <p className="text-ink-500 mt-1 font-sans text-[14px]">
            Choisis ce sur quoi tu veux progresser : ils s’afficheront sur ton accueil.
          </p>
        )}
        {step === 2 && <p className="text-ink-500 mt-1 font-sans text-[14px]">Étape 2 sur 2</p>}
      </div>

      {/* Contenu */}
      {step === 1 ? (
        <ObjectivePicker
          studentLevel={studentLevel}
          selectedIds={selectedIds}
          onToggle={(id, template) => toggleId(id, template)}
        />
      ) : (
        <PackSelectionStep
          templates={templatesNeedingPack}
          selections={packSelections}
          onChange={updatePackSelection}
        />
      )}

      {/* Actions */}
      <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button
          variant="ghost"
          className="w-full sm:w-auto"
          onClick={() => navigate({ to: '/student/dashboard' })}
        >
          Annuler
        </Button>

        {step === 1 ? (
          <Button
            className="w-full sm:w-auto"
            onClick={needsPackStep ? handleGoToStep2 : handleConfirm}
            disabled={selectedIds.length === 0}
          >
            {needsPackStep ? `Suivant (${selectedIds.length})` : `Valider (${selectedIds.length})`}
          </Button>
        ) : (
          <Button className="w-full sm:w-auto" onClick={handleConfirm} disabled={!step2Valid}>
            Valider ({selectedIds.length})
          </Button>
        )}
      </div>
    </div>
  );
}
